import type { LoomVerifiedAircraftTransitionAsset } from "../features/parametric-geometry/loomAircraftTransitionAsset";
import { getAuthToken } from "./client";
import { verifyLoomDerivedSurfaceBuffer, type LoomVerifiedDerivedSurface } from "./loomDerivedSurfaceAsset";

export const LOOM_DERIVED_SURFACE_REQUEST_SCHEMA = "loom.aircraft-derived-surface-request/v1" as const;
export const LOOM_DERIVED_SURFACE_DRIVER = "curvature" as const;
export const LOOM_DERIVED_SURFACE_ACCEPTED_SCHEMA = "loom.accepted-aircraft-occurrence-surface/v3" as const;
export const LOOM_DERIVED_SURFACE_EVALUATOR = "loom.native-aircraft-occurrence-transition-union/v2" as const;
export const LOOM_DERIVED_SURFACE_TOPOLOGY = "ordered-aircraft-occurrence-transition-continuity-union" as const;
export const LOOM_DERIVED_SURFACE_UNION = "loom.implicit-guided-explicit-transition-union/v2" as const;

export interface LoomDerivedSurfaceRequest {
  schema: typeof LOOM_DERIVED_SURFACE_REQUEST_SCHEMA;
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  accepted: {
    acceptedSchema: typeof LOOM_DERIVED_SURFACE_ACCEPTED_SCHEMA;
    definitionRevision: string;
    evaluatorId: typeof LOOM_DERIVED_SURFACE_EVALUATOR;
    topologyId: typeof LOOM_DERIVED_SURFACE_TOPOLOGY;
    unionAlgorithmId: typeof LOOM_DERIVED_SURFACE_UNION;
    geometryRevision: string;
    topologyRevision: string;
    assemblyTopologyRevision: string;
    poseRevision: string;
    sceneRevision: string;
  };
  driver: typeof LOOM_DERIVED_SURFACE_DRIVER;
}

type Row = Record<string, unknown>;
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;
const SHA = /^sha256:[0-9a-f]{64}$/;
function object(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Row;
}
function exact(row: Row, keys: readonly string[], path: string): void {
  if (Object.keys(row).length !== keys.length || Object.keys(row).some((key) => !keys.includes(key))) {
    throw new Error(`${path} has an unsupported field set`);
  }
}
function token(value: unknown, path: string): string {
  if (typeof value !== "string" || !TOKEN.test(value)) throw new Error(`${path} is invalid`);
  return value;
}
function revision(value: unknown, path: string): string {
  if (typeof value !== "string" || !SHA.test(value)) throw new Error(`${path} is not a SHA-256 revision`);
  return value;
}

export function parseLoomDerivedSurfaceRequest(value: unknown): LoomDerivedSurfaceRequest {
  const root = object(value, "derived surface request");
  exact(root, ["schema", "requestId", "sourceDefinition", "accepted", "driver"], "derived surface request");
  if (root.schema !== LOOM_DERIVED_SURFACE_REQUEST_SCHEMA || root.driver !== LOOM_DERIVED_SURFACE_DRIVER) {
    throw new Error("derived surface request markers are unsupported");
  }
  const source = object(root.sourceDefinition, "sourceDefinition");
  exact(source, ["documentId", "revision", "stateId"], "sourceDefinition");
  if (!Number.isSafeInteger(source.revision) || Number(source.revision) < 0) throw new Error("sourceDefinition.revision is invalid");
  const accepted = object(root.accepted, "accepted");
  exact(accepted, ["acceptedSchema", "definitionRevision", "evaluatorId", "topologyId", "unionAlgorithmId",
    "geometryRevision", "topologyRevision", "assemblyTopologyRevision", "poseRevision", "sceneRevision"], "accepted");
  if (accepted.acceptedSchema !== LOOM_DERIVED_SURFACE_ACCEPTED_SCHEMA
      || accepted.evaluatorId !== LOOM_DERIVED_SURFACE_EVALUATOR
      || accepted.topologyId !== LOOM_DERIVED_SURFACE_TOPOLOGY
      || accepted.unionAlgorithmId !== LOOM_DERIVED_SURFACE_UNION) throw new Error("accepted primal markers are unsupported");
  return { schema: LOOM_DERIVED_SURFACE_REQUEST_SCHEMA, requestId: token(root.requestId, "requestId"),
    sourceDefinition: { documentId: token(source.documentId, "sourceDefinition.documentId"),
      revision: Number(source.revision), stateId: token(source.stateId, "sourceDefinition.stateId") },
    accepted: { acceptedSchema: LOOM_DERIVED_SURFACE_ACCEPTED_SCHEMA,
      definitionRevision: revision(accepted.definitionRevision, "accepted.definitionRevision"),
      evaluatorId: LOOM_DERIVED_SURFACE_EVALUATOR, topologyId: LOOM_DERIVED_SURFACE_TOPOLOGY,
      unionAlgorithmId: LOOM_DERIVED_SURFACE_UNION,
      geometryRevision: revision(accepted.geometryRevision, "accepted.geometryRevision"),
      topologyRevision: revision(accepted.topologyRevision, "accepted.topologyRevision"),
      assemblyTopologyRevision: revision(accepted.assemblyTopologyRevision, "accepted.assemblyTopologyRevision"),
      poseRevision: revision(accepted.poseRevision, "accepted.poseRevision"),
      sceneRevision: revision(accepted.sceneRevision, "accepted.sceneRevision") }, driver: LOOM_DERIVED_SURFACE_DRIVER };
}

export function deriveLoomDerivedSurfaceRequest(input: {
  asset: LoomVerifiedAircraftTransitionAsset;
  documentId: string;
  requestId: string;
  sourceRevision: number;
  sourceStateId: string;
}): LoomDerivedSurfaceRequest {
  if (input.asset.header.version !== 3) throw new Error("derived display requires one accepted v3 primal surface");
  const header = input.asset.header; const binding = header.definitionBinding;
  return parseLoomDerivedSurfaceRequest({ schema: LOOM_DERIVED_SURFACE_REQUEST_SCHEMA, requestId: input.requestId,
    sourceDefinition: { documentId: input.documentId, revision: input.sourceRevision, stateId: input.sourceStateId },
    accepted: { acceptedSchema: LOOM_DERIVED_SURFACE_ACCEPTED_SCHEMA, definitionRevision: binding.definitionRevision,
      evaluatorId: LOOM_DERIVED_SURFACE_EVALUATOR, topologyId: LOOM_DERIVED_SURFACE_TOPOLOGY,
      unionAlgorithmId: LOOM_DERIVED_SURFACE_UNION, geometryRevision: header.surface.geometryRevision,
      topologyRevision: header.surface.topologyRevision, assemblyTopologyRevision: binding.assemblyTopologyRevision,
      poseRevision: binding.poseRevision, sceneRevision: header.cacheReceipt.sceneRevision }, driver: LOOM_DERIVED_SURFACE_DRIVER });
}

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
async function failure(response: Response): Promise<Error> {
  let detail = "derived surface request failed";
  try { const value = await response.clone().json() as { detail?: unknown }; detail = typeof value.detail === "string" ? value.detail : JSON.stringify(value.detail ?? value); }
  catch { detail = await response.text().catch(() => detail); }
  return new Error(`Derived surface ${response.status}: ${detail}`);
}
export async function requestLoomDerivedSurface(input: {
  asset: LoomVerifiedAircraftTransitionAsset; documentId: string; projectId: string;
  sourceRevision: number; sourceStateId: string; signal?: AbortSignal; fetcher?: typeof fetch;
}): Promise<LoomVerifiedDerivedSurface> {
  const request = deriveLoomDerivedSurfaceRequest({ ...input, requestId: crypto.randomUUID() });
  const token = await getAuthToken(); const fetcher = input.fetcher ?? fetch;
  const response = await fetcher(`${API_BASE}/thread/v1/projects/${encodeURIComponent(input.projectId)}/aero/loom/rebuilds/derived-surfaces`, {
    method: "POST", credentials: "include", signal: input.signal,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await failure(response);
  if (!response.headers.get("Content-Type")?.startsWith("application/vnd.loom.aircraft-derived-surface")
      || response.headers.get("X-Loom-Derived-Schema") !== "loom.aircraft-derived-surface/v1"
      || response.headers.get("X-Loom-Derived-Driver") !== "curvature"
      || response.headers.get("X-Loom-Section-Policy") !== "pinned-primal-accepted-geometry-only/v1") {
    throw new Error("Derived surface response authority is unsupported");
  }
  const parsed = await verifyLoomDerivedSurfaceBuffer(await response.arrayBuffer(), request);
  const required = (name: string) => { const value = response.headers.get(name); if (!value) throw new Error(`Derived surface response is missing ${name}`); return value; };
  if (required("X-Loom-Source-Definition-Revision") !== request.accepted.definitionRevision
      || required("X-Loom-Source-Geometry-Revision") !== request.accepted.geometryRevision
      || required("X-Loom-Source-Topology-Revision") !== request.accepted.topologyRevision
      || required("X-Loom-Source-Pose-Revision") !== request.accepted.poseRevision
      || required("X-Loom-Retained-Runtime-Binding-Revision") !== parsed.header.sourceBinding.retainedRuntimeBindingRevision
      || required("X-Loom-Derived-Geometry-Revision") !== parsed.header.surface.geometryRevision
      || required("X-Loom-Derived-Topology-Revision") !== parsed.header.surface.topologyRevision
      || required("X-Loom-Derived-Node-Revision") !== parsed.header.surface.nodeRevision
      || !["miss", "hit", "pose-reuse"].includes(required("X-Loom-Derived-Cache"))
      || response.headers.get("Cache-Control") !== "private, no-store") {
    throw new Error("Derived surface response headers disagree with the verified asset");
  }
  const buildMs = Number(required("X-Loom-Derived-Build-Ms"));
  const insertCount = Number(required("X-Loom-Derived-Insert-Count")); const seed = Number(required("X-Loom-Derived-Seed"));
  if (!Number.isFinite(buildMs) || buildMs < 0 || !Number.isSafeInteger(insertCount) || insertCount < 0
      || !Number.isSafeInteger(seed) || seed < 0 || insertCount !== parsed.header.mesherReceipt.insertCount
      || seed !== parsed.header.mesherReceipt.seed) throw new Error("Derived surface response metrics are invalid");
  return { ...parsed, transport: { buildMs, cache: required("X-Loom-Derived-Cache") as "miss" | "hit" | "pose-reuse", insertCount, seed } };
}
